import React, { useState, useEffect, useMemo } from 'react';
import { FaTimes as X } from 'react-icons/fa';
import axios from 'axios';
import { exportToExcel } from './excelGenerate';
import { API_ROOT } from '../service/Services';
import { useNavigate } from 'react-router-dom';
import Spinner from '../components/Spinner';
import { exportToPDF } from './pdfGenerate';

const ProductRow = ({ product, original, onUpdate, onRemove, uniqueId }) => {
  const handleChange = (field, value) => {
    let updates = {};

    if (field === 'name' || field === 'remarks') {
      updates[field] = value;
    } else if (value === '') {
      updates[field] = '';
      if (['quantity', 'rate', 'percent'].includes(field)) {
        updates.total = '';
        if (field === 'percent') updates.rate = '';
      }
    } else {
      const parsed = parseFloat(value);

      if (field === 'rate') {
        const originalRate = parseFloat(original.rate) || 0;
        const percentChange = originalRate !== 0
          ? ((parsed - originalRate) / originalRate) * 100
          : 0;
        const quantity = parseFloat(product.quantity) || 0;
        updates.rate = parsed;
        updates.percent = parseFloat(percentChange.toFixed(2));
        updates.total = parseFloat((parsed * quantity).toFixed(2));
      } else if (field === 'percent') {
        const originalRate = parseFloat(original.rate) || 0;
        const rate = originalRate + (originalRate * parsed) / 100;
        const quantity = parseFloat(product.quantity) || 0;
        updates.percent = parsed;
        updates.rate = parseFloat(rate.toFixed(2));
        updates.total = parseFloat((rate * quantity).toFixed(2));
      } else {
        updates[field] = parsed;

        if (field === 'quantity') {
          const rate = parseFloat(product.rate) || 0;
          updates.total = parseFloat((parsed * rate).toFixed(2));
        }
      }
    }

    onUpdate(uniqueId, updates);
  };

  const isModified = ['name', 'quantity', 'rate', 'percent', 'total', 'gst', 'remarks'].some(
    (field) => (product[field] ?? '') !== (original?.[field] ?? '')
  );

  const getValue = (field) => {
    if (field === 'remarks') return product[field] ?? '';
    return product[field] === '' || product[field] === undefined || product[field] === null
      ? ''
      : product[field];
  };

  return (
    <tr className={`border-b ${isModified ? 'bg-yellow-50' : 'bg-white'}`}>
      {['name', 'quantity', 'rate', 'percent', 'total', 'gst', 'remarks'].map((field) => (
        <td key={field} className="p-3 text-sm">
          {field === 'total' ? (
            getValue(field)
          ) : field === 'gst' ? (
            <select
              value={getValue(field) !== undefined && getValue(field) !== null ? getValue(field) : ''}
              onChange={(e) => handleChange(field, parseFloat(e.target.value))}
              className="w-full p-2 text-sm border rounded-lg font-medium text-base bg-white"
              style={{ minWidth: '70px', height: '40px' }}
            >
              <option value="">Select</option>
              {[0, 5, 12, 18, 28].map(val => (
                <option key={val} value={val}>{val}%</option>
              ))}
            </select>
          ) : (
            <input
              type={field === 'remarks' || field === 'name' ? 'text' : 'number'}
              value={getValue(field)}
              onChange={(e) => handleChange(field, e.target.value)}
              onFocus={(e) => e.target.select()}
              className="w-full p-2 text-sm border rounded-lg"
              placeholder={field === 'remarks' ? 'Add remarks' : undefined}
            />
          )}
        </td>
      ))}



      <td className="p-3 text-sm">
        <button
          onClick={() => onRemove(uniqueId)}
          className="text-red-500 hover:text-red-700"
          title="Remove item"
        >
          <X size={16} />
        </button>
      </td>
    </tr>
  );
};

const InvoiceTable = ({ data, company }) => {
  const [originalData, setOriginalData] = useState(null);
  const [modifiedData, setModifiedData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    if (data?.success) {
      const initialized = {
        ...data,
        data: data.data.map((entry) => ({
          ...entry,
          items: entry.items.map((item) => {
            const quantity = Number(item.quantity) || 1;
            const rate = Number(item.rate) || 0;
            return {
              ...item,
              quantity,
              rate,
              total: quantity * rate,
              percent: item.percent || 0,
              uniqueId: `${item._id}-${Math.random().toString(36).substr(2, 9)}`
            };
          }),
        })),
      };
      setOriginalData(initialized);
      setModifiedData(initialized);
    }
  }, [data]);

  const allItems = useMemo(() => {
    if (!modifiedData?.data) return [];
    return modifiedData.data.flatMap((d) => d.items);
  }, [modifiedData]);

  const originalItemsMap = useMemo(() => {
    if (!originalData?.data) return {};
    const map = {};
    originalData.data.flatMap(d => d.items).forEach(item => {
      map[item.uniqueId] = item;
    });
    return map;
  }, [originalData]);





  const isDataModified = useMemo(() => {
    // Check for modified items
    const itemsModified = allItems.some(item => {
      const original = originalItemsMap[item.uniqueId];
      if (!original) return false; // New items are handled separately
      return ['name', 'quantity', 'rate', 'percent', 'total', 'gst', 'remarks'].some(
        (field) => (item[field] ?? '') !== (original[field] ?? '')
      );
    });

    // Check for deleted items (present in original but not in current)
    const originalItems = originalData?.data.flatMap(d => d.items) || [];
    const currentIds = new Set(allItems.map(item => item.uniqueId));
    const itemsDeleted = originalItems.some(item => !currentIds.has(item.uniqueId));

    // Check for new items (present in current but not in original)
    const originalIds = new Set(originalItems.map(item => item.uniqueId));
    const itemsAdded = allItems.some(item => !originalIds.has(item.uniqueId));

    return itemsModified || itemsDeleted || itemsAdded;
  }, [allItems, originalItemsMap, originalData]);








  const handleRemove = (uniqueId) => {
    setModifiedData(prev => {
      const updated = { ...prev };
      updated.data = updated.data.map(entry => ({
        ...entry,
        items: entry.items.filter(item => item.uniqueId !== uniqueId)
      }));
      return updated;
    });
  };

  const handleUpdate = (uniqueId, updates) => {
    setModifiedData(prev => {
      const updated = { ...prev };
      updated.data = updated.data.map(entry => ({
        ...entry,
        items: entry.items.map(item => {
          if (item.uniqueId === uniqueId) {
            return { ...item, ...updates };
          }
          return item;
        })
      }));
      return updated;
    });
  };

  const handleSave = async () => {
    if (!data?.obj_id) return alert('Missing quotation ID');

    try {
      setIsSaving(true);
      const res = await axios.put(`${API_ROOT}/quotation/${data.obj_id}`, {
        structuredData: modifiedData.data,
      });

      if (res.data.success) {
        alert('Changes saved successfully');
        setOriginalData(JSON.parse(JSON.stringify(modifiedData)));
      } else {
        alert('Failed to save');
      }
    } catch (err) {
      console.error(err);
      alert('Error while saving');
    } finally {
      setIsSaving(false);
    }
  };

  const totalAmount = useMemo(() => {
    return allItems.reduce((sum, item) => sum + (Number(item.total) || 0), 0);
  }, [allItems]);

  const handleExport = () => {
    if (modifiedData?.success) {
      exportToExcel(modifiedData, company, setLoading, navigate);
    }
  };
    const handleExportPDF = () => {
    if (modifiedData?.success) {
      exportToPDF(modifiedData, company, setLoading, navigate);
    }
  };
  

  if (!modifiedData || !originalData) return null;

  return (
    <div className="container mx-auto p-6">
      <h2 className="text-2xl font-bold mb-4">Invoice Items</h2>

      <table className="min-w-full bg-white border border-gray-300 shadow rounded-lg">
        <thead className="bg-gray-100">
          <tr>
            {['Name', 'Quantity', 'Base Rate', 'Percent', 'Total', 'GST %', 'Remarks', 'Actions'].map((head, i) => (
              <th key={i} className="p-3 text-left text-sm font-semibold text-gray-700">
                {head}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {allItems.map((item) => (
            <ProductRow
              key={item.uniqueId}
              product={item}
              original={originalItemsMap[item.uniqueId]}
              onUpdate={handleUpdate}
              onRemove={handleRemove}
              uniqueId={item.uniqueId}
            />
          ))}
        </tbody>
      </table>

      <div className="mt-6 flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="text-lg font-semibold">
          Total Amount: <span className="text-indigo-600">{totalAmount.toFixed(2)}</span>
        </div>

        <div className="flex gap-3">
          {loading && <Spinner/>}

          <button
            onClick={handleExport}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition"
            disabled={loading}
          >
            {loading ? "Exporting..." : "Export to Excel"}
          </button>

             <button
            onClick={handleExportPDF}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition"
            disabled={loading}
          >
            {loading ? "Exporting..." : "Export to PDF"}
          </button>

          <button
            onClick={handleSave}
            disabled={!isDataModified || isSaving}
            className={`px-4 py-2 rounded-md text-white ${isDataModified && !isSaving
              ? 'bg-blue-600 hover:bg-blue-700'
              : 'bg-gray-400 cursor-not-allowed'
              } transition`}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvoiceTable;